import React from 'react';
import { useMutation } from '@apollo/client';
import { SET_LESSON_BOOKMARK } from '../../../queries';
import { useHubPetParent } from '../HubPetParentContext';
import { track } from '../analytics';
import { bookmarkToast, mergeBookmarks } from './bookmarkState';

export interface LessonBookmarksApi {
  bookmarks: Set<string>;
  isBookmarked: (slug: string) => boolean;
  toggle: (slug: string, lessonName: string) => void;
  toast: string | null;
  dismissToast: () => void;
  error: string | null;
  dismissError: () => void;
}

// One shared source of bookmark state for every hub surface (grid card, detail
// header, scroll-away bar) so they can never disagree. Server state comes from
// the pet parent query; in-flight toggles are overlaid optimistically and
// reverted if the mutation fails.
//
// `email` is not passed on the event: it is registered as a Mixpanel super
// property in Hub/index.tsx, so it rides along on every event already.
export default function useLessonBookmarks(): LessonBookmarksApi {
  const { petParent } = useHubPetParent();
  const [optimistic, setOptimistic] = React.useState<Record<string, boolean>>({});
  const [toast, setToast] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [setLessonBookmark] = useMutation(SET_LESSON_BOOKMARK);

  const bookmarks = React.useMemo(
    () => mergeBookmarks(petParent?.bookmarkedLessons, optimistic),
    [petParent?.bookmarkedLessons, optimistic],
  );

  const clearOptimistic = (slug: string) => {
    setOptimistic((s) => {
      const next = { ...s };
      delete next[slug];
      return next;
    });
  };

  const toggle = (slug: string, lessonName: string) => {
    const bookmarked = !bookmarks.has(slug);
    setOptimistic((s) => ({ ...s, [slug]: bookmarked }));
    setToast(bookmarkToast(bookmarked));
    track('lesson_bookmark', { lesson_name: lessonName, bookmarked });

    setLessonBookmark({
      variables: { slug, bookmarked },
      // The mutation returns the full list; make it the cached server state so
      // the optimistic overlay can be dropped once settled.
      update: (cache, { data }) => {
        const list = data?.setLessonBookmark;
        if (list && petParent?.id) {
          cache.modify({
            id: cache.identify({ __typename: 'PetParentSingular', id: petParent.id }),
            fields: { bookmarkedLessons: () => list },
          });
        }
      },
    })
      .then(() => clearOptimistic(slug))
      .catch(() => {
        // Revert to server truth so the UI never silently shows unsaved state,
        // and drop the confirmation so it can't sit next to the failure notice.
        clearOptimistic(slug);
        setToast(null);
        setError("We couldn't save that bookmark — please try again.");
      });
  };

  return {
    bookmarks,
    isBookmarked: (slug: string) => bookmarks.has(slug),
    toggle,
    toast,
    dismissToast: () => setToast(null),
    error,
    dismissError: () => setError(null),
  };
}
